/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import moment from "moment"
import numeral from "numeral"
import { Appearance, TextInput, ViewStyle } from "react-native"

import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Field, Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { PickerField } from "../../field/picker-field/picker-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { View } from "native-base"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { useMemo } from "react"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"


const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

export interface HarvestFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
   initialValues?: {
    farm_id?: number
    cycle_id?: number
    harvested_at?: string
    status?: string
    weight?: number
    size?: number
    selling_price?: number
    price?: number
    buyer?: string
    remark?: string,
  }
}

/**
 * Describe your component here
 */
export const HarvestForm = observer(function HarvestForm(props: HarvestFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const harvestStatuses = useMemo(() => [
    { id: 'Full', name: translate("harvestForm.full") },
    { id: 'Partial', name: translate("harvestForm.partial") },
    { id: 'Failed', name: translate("harvestForm.failed") },
  ], [])

  const input = {
    weight: React.useRef<TextInput>(),
    price: React.useRef<TextInput>(),
    size: React.useRef<TextInput>(),
    buyer: React.useRef<TextInput>(),
    remark: React.useRef<TextInput>(),
  }

  const nextInput = {
    weight: () => input.size.current && input.size.current.focus(),
    size: () => input.price.current && input.price.current.focus(),
    price: () => input.buyer.current && input.buyer.current.focus(),
    buyer: () => input.remark.current && input.remark.current.focus(),
  }

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("harvestForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("harvestForm.cycleId")),
    harvested_at: yup.mixed().nullable().required().label(translate("harvestForm.harvestedAt")),
    status: yup.string().nullable().required().label(translate("harvestForm.status")),
    weight: yup.number().moreThan(0).nullable().required().label(translate("harvestForm.weight")),
    size: yup.number().min(10).nullable().required().label(translate("harvestForm.size")),
    price: yup.number().moreThan(0).nullable().required().label(translate("harvestForm.price")),
    selling_price: yup.number().nullable().required().label(translate("harvestForm.sellingPrice")),
    remark: yup.string().ascii().max(255).nullable().label(translate("harvestForm.remark")),
  })

  const initialValues = useMemo<HarvestFormProps["initialValues"]>(() => ({
    farm_id: null,
    pond_id: null,
    ...initialValuesProps,
    harvested_at: initialValuesProps.harvested_at || moment().format('YYYY-MM-DD'),
    weight: initialValuesProps.weight || null,
    size: initialValuesProps.size || null,
    buyer: initialValuesProps.buyer || null,
    remark: initialValuesProps.remark || null,
    selling_price: initialValuesProps.selling_price || null,
    status: initialValuesProps.status || null,
    get price() {
      if (this.selling_price != null && this.weight != null)
        return this.selling_price / this.weight;
      return null;
    }
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="harvestForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue("cycle_id", null)
                await setFieldValue("farm_id", value || null)
                await setFieldTouched("farm_id")
              },
            }}
          />
          <WalkthroughElement
            id="harvest-form"
            scrollViewRef={scrollViewRef}
            scrollOffset={125}
          >
            <View style={TITLE}>
              <Text tx="harvestForm.harvest" preset="title3" colorScheme={colorScheme} />
            </View>
            <View style={BORDER} />
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                titleTx: "harvestForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="harvested_at"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <PickerField
              name="status"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              pickerProps={{ data: harvestStatuses, titleTx: "harvestForm.statusPlaceholder" }}
            />
            <NumeralField
              FieldComponent={Field}
              name="weight"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              numeralInputProps={{
                onChangeNumber: async (value) => {
                  await setFieldValue("weight", value || null)
                  await setFieldTouched("weight")
                  if (value != null && values.price != null)
                    await setFieldValue("selling_price", value * values.price)
                  else await setFieldValue("selling_price", null)
                }
              }}
            />
            <NumeralField
              name="size"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              // numeralInputProps={{ format: '10,0' }}
            />
            <NumeralField
              FieldComponent={Field}
              name="price"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true, label: `${translate("harvestForm.price")} (${initialValues.currency})` }}
              numeralInputProps={{
                onChangeNumber: async (value) => {
                  await setFieldValue("price", value || null)
                  await setFieldTouched("price")
                  if (value != null && values.weight != null)
                    await setFieldValue("selling_price", value * values.weight)
                  else await setFieldValue("selling_price", null)
                }
              }}
            />
            <TextField
              name="selling_price"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              formControlProps={{
                isRequired: true,
                children: <Text text={`${initialValues.currency}  ${numeral(values.selling_price).format('0,0.[00]')}`} preset="title4" colorScheme={colorScheme} />,
                inputProps: {
                  // value: `${initialValues.currency}  ${numeral(values.selling_price).format('0,0.[00]')}`,
                  isReadOnly: true,
                  borderWidth: 0,
                }
              }}
            />
            <TextField
              name="buyer"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <TextField
              name="remark"
              txPrefix="harvestForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

HarvestForm.displayName = 'HarvestForm'
